import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Plane, GraduationCap, Shield, Target, Award, ChevronRight, ClipboardCheck, Wrench, Bell } from "lucide-react";
import { isFleetDomain } from "../config/productConfig";
import { resolveHomePath } from "../utils/roleRedirect";

const FLEET_COPY = {
  appName: "Aerofleet Center",
  brandName: "Aerofleet Center",
  badge: "Gestão de Frota e Conformidade",
  headlineBefore: "Mantenha a sua ",
  headlineHighlight: "frota",
  headlineAfter: " sempre em dia",
  subtitle:
    "Plataforma de gestão de frota, manutenções e licenças para escolas e operadores. Alertas automáticos, documentação centralizada e conformidade sem esforço. Experimente 14 dias grátis.",
  ctaLabel: "Pronto para descolar",
  features: [
    {
      icon: <Wrench className="w-6 h-6" />,
      title: "Manutenção",
      description: "Acompanhe horas de voo, inspeções e alertas de manutenção por aeronave.",
    },
    {
      icon: <ClipboardCheck className="w-6 h-6" />,
      title: "Licenças e Seguros",
      description: "Validade de licenças, proficiência de pilotos e apólices num só painel.",
    },
    {
      icon: <Bell className="w-6 h-6" />,
      title: "Alertas Automáticos",
      description: "Notificações antes de expirar um documento ou vencer uma manutenção.",
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Conformidade",
      description: "Documentação centralizada, sempre pronta para inspeção.",
    },
  ],
  footerText: "Plataforma de gestão de frota e conformidade",
};

const TRAINING_COPY = {
  appName: "UTC",
  brandName: "ULM Training Centre",
  badge: "Sistema de Treino para Pilotos ULM",
  headlineBefore: "Prepare-se para ",
  headlineHighlight: "Voar",
  headlineAfter: "",
  subtitle:
    "Plataforma de treino adaptativo para exames de pilotos de ultraleves. Estude de forma inteligente, acompanhe o seu progresso e conquiste os céus.",
  ctaLabel: "Começar Agora",
  features: [
    {
      icon: <Target className="w-6 h-6" />,
      title: "Treino Adaptativo",
      description: "Questões personalizadas com base no seu desempenho e áreas a melhorar.",
    },
    {
      icon: <GraduationCap className="w-6 h-6" />,
      title: "Exames Simulados",
      description: "Simule o exame real com cronómetro e avaliação por disciplina.",
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: "Gamificação",
      description: "Conquiste badges e acompanhe o seu progresso como piloto.",
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Acompanhamento",
      description: "Instrutores podem monitorizar e criar exames personalizados.",
    },
  ],
  footerText: "Plataforma de treino para pilotos de ultraleves em Portugal",
};

const LandingPage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const isFleet = isFleetDomain();
  const copy = isFleet ? FLEET_COPY : TRAINING_COPY;
  // Set by AuthCallback when the account isn't allowed on this domain
  const [loginError] = useState(() => {
    try {
      const msg = sessionStorage.getItem("login_error");
      sessionStorage.removeItem("login_error");
      return msg;
    } catch (e) {
      return null;
    }
  });

  // Redirect authenticated users to their appropriate dashboard
  useEffect(() => {
    if (!loading && user) {
      navigate(resolveHomePath(user), { replace: true });
    }
  }, [user, loading, navigate]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Use current origin for redirect - AuthCallback will handle role-based routing
    const redirectUrl = window.location.origin;
    // app_name customizes the login page title
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}&app_name=${encodeURIComponent(copy.appName)}`;
  };

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-cockpit-bg flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Don't render landing page if user is authenticated (will redirect via useEffect)
  if (user) {
    return (
      <div className="min-h-screen bg-cockpit-bg flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cockpit-bg relative overflow-hidden">
      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-20"
        style={{ 
          backgroundImage: `url('https://images.unsplash.com/photo-1730303642985-71aea56a033c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHx1bHRyYWxpZ2h0JTIwYWlyY3JhZnQlMjBmbHlpbmclMjBzdW5zZXR8ZW58MHx8fHwxNzY2MzU1NDkzfDA&ixlib=rb-4.1.0&q=85')` 
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-cockpit-bg/80 via-cockpit-bg/90 to-cockpit-bg" />
      
      {/* Content */}
      <div className="relative z-10">
        {loginError && (
          <div
            className="bg-red-500/15 border-b border-red-500/40 text-red-200 text-sm text-center px-4 py-3"
            data-testid="login-error"
          >
            {loginError}
          </div>
        )}
        {/* Header */}
        <header className="container mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                <Plane className="w-6 h-6 text-white" />
              </div>
              <span className="font-chivo text-xl text-cockpit-text-bright tracking-tight">{copy.brandName}</span>
            </div>
            <Button 
              onClick={handleLogin}
              data-testid="header-login-btn"
              className="btn-cockpit"
            >
              Entrar
            </Button>
          </div>
        </header>

        {/* Hero Section */}
        <section className="container mx-auto px-6 pt-20 pb-32">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/10 border border-orange-500/30 rounded-full mb-8">
              <span className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></span>
              <span className="text-orange-400 text-sm font-mono">{copy.badge}</span>
            </div>

            <h1 className="font-chivo text-4xl sm:text-5xl lg:text-6xl font-bold text-cockpit-text-bright mb-6 leading-tight">
              {copy.headlineBefore}<span className="text-orange-500 glow-orange">{copy.headlineHighlight}</span>{copy.headlineAfter}
            </h1>

            <p className="text-lg text-cockpit-text max-w-2xl mx-auto mb-10">
              {copy.subtitle}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={handleLogin}
                data-testid="hero-login-btn"
                className="btn-cockpit text-base px-8 py-4 inline-flex items-center gap-2"
              >
                {copy.ctaLabel}
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="container mx-auto px-6 pb-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {copy.features.map((feature) => (
              <FeatureCard
                key={feature.title}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
              />
            ))}
          </div>
        </section>

        {!isFleet && (
          <section className="container mx-auto px-6 pb-20">
            <div className="instrument-panel">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                <StatItem value="10" label="Disciplinas" />
                <StatItem value="3000+" label="Questões" />
                <StatItem value="75%" label="Meta Aprovação" />
                <StatItem value="13" label="Badges" />
              </div>
            </div>
          </section>
        )}

        {/* Footer */}
        <footer className="container mx-auto px-6 py-8 border-t border-cockpit-border">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Plane className="w-5 h-5 text-orange-500" />
              <span className="text-sm text-cockpit-text">{copy.brandName}</span>
            </div>
            <p className="text-sm text-slate-500">
              {copy.footerText}
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

const FeatureCard = ({ icon, title, description }) => (
  <div className="instrument-panel group hover:border-orange-500/50 transition-all duration-300" data-testid={`feature-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="w-12 h-12 bg-orange-500/10 rounded-lg flex items-center justify-center text-orange-500 mb-4 group-hover:bg-orange-500/20 transition-colors">
      {icon}
    </div>
    <h3 className="font-chivo text-lg text-cockpit-text-bright mb-2">{title}</h3>
    <p className="text-sm text-cockpit-text">{description}</p>
  </div>
);

const StatItem = ({ value, label }) => (
  <div className="text-center">
    <div className="font-mono text-3xl md:text-4xl text-orange-400 glow-orange mb-1">{value}</div>
    <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
  </div>
);

export default LandingPage;
